'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "inputClassName", "match", "matched", "onChange", "onMatchedChange", "size", "value"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import SyntheticEvent from '../core/SyntheticEvent';
import { inputCouldMatch } from '../core/Strings';
import { getTextHeight } from '../utils/MeasureText';
import UITextArea from './UITextArea';
import styled, { css } from 'styled-components';
import { EERIE } from 'HubStyleTokens/colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const xxlSizeMixin = css(["font-size:32px;line-height:40px;padding:16px;"]);
const getSize = ({
  size
}) => size === 'xxl' && xxlSizeMixin;
const Wrapper = styled.div.withConfig({
  displayName: "UIMatchTextArea__Wrapper",
  componentId: "sc-1bo89vr-0"
})(["position:relative;width:100%;"]);
const PlaceholderText = styled.div.withConfig({
  displayName: "UIMatchTextArea__PlaceholderText",
  componentId: "sc-1bo89vr-1"
})(["&&{border-color:transparent;color:", ";position:relative;word-wrap:break-word;", "}"], EERIE, getSize);
const TextArea = styled(UITextArea).withConfig({
  displayName: "UIMatchTextArea__TextArea",
  componentId: "sc-1bo89vr-2"
})(["&&{background-color:transparent;left:0;position:absolute;top:0;width:100%;", "}"], getSize);
class UIMatchTextArea extends Component {
  constructor(props) {
    super(props);
    /**
     * Measure the match text to determine the height of the <textarea>.
     */
    this.computeMatchTextHeight = () => {
      const {
        props: {
          inputRef,
          match
        }
      } = this;
      const inputEl = inputRef.current;
      if (!inputEl) return;
      const originalInputValue = inputEl.value;
      inputEl.value = match;
      const matchTextHeight = getTextHeight(inputEl);
      inputEl.value = originalInputValue;
      if (matchTextHeight !== this.state.matchTextHeight) {
        this.setState({
          matchTextHeight
        });
      }
    };
    this.handleChange = evt => {
      const {
        props: {
          match,
          matched,
          onChange,
          onMatchedChange
        }
      } = this;
      const {
        target: {
          value
        }
      } = evt;
      if (!inputCouldMatch(value, match)) {
        return;
      }
      onChange(evt);
      const newMatched = value === match;
      if (newMatched !== matched) {
        onMatchedChange(SyntheticEvent(newMatched));
      }
    };
    this.state = {
      matchTextHeight: null
    };
  }
  componentDidMount() {
    // Handle case where initial "value" and "match" props are equal.
    const {
      match,
      onMatchedChange,
      value
    } = this.props;
    if (value === match) {
      onMatchedChange(SyntheticEvent(value === match));
    }
    this.computeMatchTextHeight();
    addEventListener('resize', this.computeMatchTextHeight);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.match !== this.props.match) {
      this.computeMatchTextHeight();
    }
  }
  componentWillUnmount() {
    removeEventListener('resize', this.computeMatchTextHeight);
  }
  render() {
    const _this = this,
      {
        props: {
          className,
          inputClassName,
          match,
          size,
          value
        },
        state: {
          matchTextHeight
        }
      } = _this,
      rest = _objectWithoutPropertiesLoose(_this.props, _excluded);
    const style = {
      height: matchTextHeight
    };
    return /*#__PURE__*/_jsxs(Wrapper, {
      className: className,
      children: [/*#__PURE__*/_jsx(PlaceholderText, {
        style: style,
        size: size,
        className: "form-control private-form__control",
        children: match
      }), /*#__PURE__*/_jsx(TextArea, Object.assign({}, rest, {
        className: inputClassName,
        size: size,
        "data-match-value": match,
        value: value,
        onChange: this.handleChange,
        resize: false,
        style: style
      }))]
    });
  }
}
UIMatchTextArea.displayName = 'UIMatchTextArea';
UIMatchTextArea.propTypes = Object.assign({}, UITextArea.propTypes, {
  inputClassName: PropTypes.string,
  match: PropTypes.string,
  matched: PropTypes.bool,
  onMatchedChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['md', 'xxl'])
});
UIMatchTextArea.defaultProps = Object.assign({}, UITextArea.defaultProps, {
  matched: false
});
export default ShareInput(Controllable(UIMatchTextArea, ['matched', 'value']));